/**
 * --- External Imports ---
 */
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AlertTitle, Box, Button, CircularProgress, Collapse, IconButton, List, Modal, styled, Typography, useTheme } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import { ConditionMode, Form, FormErrorBoundary, FormErrorBoundaryMode, IConditionConfigurationContext, IConditionView, IFormActions, IFormState, IFormSubmit, IFormSubmitValidationFailed, IFormValidate, useField, useFormActions, useFormState, ValidateOn } from "@ngt/forms-core";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * --- Internal Imports ---
 */
import { grey } from '@mui/material/colors';
import dayjs from 'dayjs';
import { DeleteSiteEvent, NewPostInsertSiteEvent, SiteEventType, Species } from '../../api/dtos/dtos';
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from '../../constants/styles/AlertConstants';
import ServiceStackClientContext from '../../contexts/api/ServiceStackContext';
import AuthContext from '../../contexts/auth/AuthContext';
import AppSettingsContext from '../../contexts/data/AppSettingsContext';
import MapContext, { MapEvent } from '../../contexts/data/MapContext';
import MapFormContext from '../../contexts/data/MapFormContext';
import { SpeciesContext } from '../../contexts/data/SpeciesContext';
import SpeciesFormsContext from '../../contexts/data/SpeciesFormsContext';
import AlertContext from '../../contexts/styles/AlertContext';
import { capitaliseKeys } from '../../extensions/ObjectExtensions';
import { sendConversion } from '../../functions/pubSuite';
import useGetCoordinatesState from '../../hooks/data/useGetCoordinatesState';
import useGetSiteEventById from '../../hooks/data/useGetSiteEventById';
import useFormFieldCondition from '../../hooks/validation/useFormFieldCondition';
import useValidateSpeciesForm from '../../hooks/validation/useValidateSpeciesForm';
import Center from '../common/Center';
import asyncDebounce from "../forms/fields/utils/AsyncDebounce";
import LocationAlert from '../map/LocationAlert';
import FormActionButton from './FormActionButton';
import FormSubmitButton from './FormSubmitButton';
import SiteEventTypeRadioGroup from './site-event-type/RadioGroup';
import SpeciesFormQuestion from './SpeciesFormQuestion';
import CustomFormErrors from './utils/CustomFormErrors';
import CustomFormValues from './utils/CustomFormValues';

//
// --- Styled Components ---
//
const FieldSpacer = styled(Box)(() => ({
    position: 'relative',
    height: '15px'
}));


const BasicConditionView: IConditionView = ({
    conditionMet,
    mode,
    children
}) => {
    if (mode !== ConditionMode.Show) {
        return (
            <>
                {children}
            </>
        );
    }

    return (
        <Collapse
            in={conditionMet}
            appear={true}
        >
            {children}
        </Collapse>
    );
}

// const RemoveFromDOMConditionView: IConditionView = ({
//     conditionMet,
//     mode,
//     children
// }) => {
//     const timeout = 250;

//     const timeoutId = useRef<number>();
//     const [hide, setHide] = useState(!conditionMet);

//     useEffect(() => {
//         if (conditionMet) {
//             if (timeoutId.current) {
//                 window.clearTimeout(timeoutId.current);
//                 timeoutId.current = undefined;
//             }

//             setHide(false);
//         }

//         if (mode !== ConditionMode.Show) {
//             return;
//         }

//         if (!conditionMet && !timeoutId.current) {

//             timeoutId.current = window.setTimeout(() => {
//                 timeoutId.current = undefined;
//                 setHide(true);
//             }, timeout);
//         }
//     }, [timeout, timeoutId, conditionMet, mode, setHide]);


//     useEffect(() => {
//         return () => {
//             if (timeoutId.current) {
//                 window.clearTimeout(timeoutId.current);
//             }
//         }
//     }, [timeoutId])

//     if (mode !== ConditionMode.Show) {
//         return (
//             <>
//                 {children}
//             </>
//         );
//     }

//     if (hide) {
//         return null;
//     }

//     return (
//         <Collapse
//             in={conditionMet}
//             timeout={timeout}
//             appear={true}
//         >
//             {children}
//         </Collapse>
//     );
// }

/**
 * --- Condition Component Configuration ---
 * 
 * This is a global "Condition" componnent config designed to handle
 * how the children of a condition component are rendered. 
 */
const conditionConfiguration: IConditionConfigurationContext = {
    onConditionNotMet: () => { },
    view: BasicConditionView
}

/**
 * --- Species Form Base Content ---
 * 
 * Handles the rendering of the species forms, using the provided species 
 * form fields from the backend. 
 */
const SpeciesFormBaseContent: FC = () => {
    const theme = useTheme();

    //
    // -- Context --
    const { 
        setSiteEventType, 
        closeDrawer, 
        mapFormData, 
        setMapFormData, 
        drawerOpen, 
        recordToEdit, 
        drawerMaximised, 
        setSubSpecies, 
        siteEventType
    } = useContext(MapFormContext);
    const { selectedForm, data: allSpeciesFormData, loading: speciesFormsLoading } = useContext(SpeciesFormsContext);
    const { addAlert } = useContext(AlertContext);
    const { species: { species } } = useContext(SpeciesContext);
    const { filter, setSubmitterFilter } = useContext(MapContext);

    //
    // -- Hooks --
    const latField = useField(selectedForm?.name + ".Latitude");
    const lngField = useField(selectedForm?.name + ".Longitude");
    const form = useFormActions();
    const { value, focused } = useFormState({ value: true, focused: true });

    // 
    // -- States --
    const [siteEventValue, setSiteEventValue] = useState<string>('');

    // Pre select form for new pests
    useEffect(() => {
        if (drawerOpen && drawerMaximised &&
            (species === Species.NewPests || species == Species.Mouse)) {
            setSiteEventType(SiteEventType.Sighting);
            setSiteEventValue(SiteEventType.Sighting.toString());
        }
    }, [species, drawerOpen, drawerMaximised]);

    useEffect(() => {
        if (siteEventType == undefined && !recordToEdit?.siteEventType) {
            setSiteEventValue('');
        }
    }, [siteEventType]);

    useEffect(() => {
        if (recordToEdit?.siteEventType) {
            setSiteEventValue(recordToEdit.siteEventType.toString());
        }
    }, [recordToEdit]);

    // Generate the state request
    const stateRequest = useMemo(() => {
        if (!selectedForm || !mapFormData || !mapFormData[selectedForm.name] ||
            !mapFormData[selectedForm.name]?.["Latitude"] || !mapFormData[selectedForm.name]?.["Longitude"]) {
            return undefined;
        }
        
        var lat = Number(mapFormData[selectedForm.name]["Latitude"]);
        if (isNaN(lat) || lat < -90 || lat > 90) {
            return undefined;
        }

        var lng = Number(mapFormData[selectedForm.name]["Longitude"]);
        if (isNaN(lng) || lng < -180 || lng > 180) {
            return undefined;
        }

        return {
            latitude: mapFormData[selectedForm.name]["Latitude"],
            longitude: mapFormData[selectedForm.name]["Longitude"]
        }
    }, [mapFormData, selectedForm]);

    // get state hook
    const { data: state } = useGetCoordinatesState(
        stateRequest,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    // add state to form meta data to all is use in condition functions
    useEffect(() => {
        if (form && selectedForm) {
            form.setFieldValue(`${selectedForm!.name}.meta.state`, state);
        }
    }, [state, form, selectedForm]);

    const handleSiteEventChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const parse = parseInt(event.target.value);
        if (isNaN(parse)) {
            return;
        }

        setSiteEventType(parse);
        setSiteEventValue(event.target.value);
    }, [setSiteEventType, setSiteEventValue]);

    const handleClose = (species: Species) => {
        if (species != Species.NewPests) {
            setSiteEventValue('');
        }

        if (filter.bySubmitter.prevState) {
            setSubmitterFilter(c => ({
                ...c,
                ...filter.bySubmitter.prevState
            }));
        }
        closeDrawer();
    };

    const getCountry = async (lat: number, lng: number): Promise<string | undefined> => {
        const geocoder = new google.maps.Geocoder();

        // Wrapping geocoder.geocode in a Promise to use async/await
        const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results) {
                    resolve(results);
                } else {
                    reject(`Geocoder failed due to: ${status}`);
                }
            });
        });

        // Loop through results and find the country
        for (let value of results) {
            const addressComponents = value.address_components;
            const country = addressComponents.find(component =>
                component.types.includes("country")
            );

            if (country) {
                return country.short_name;
            }
        }

        // If no country is found
        return undefined;
    };

    useEffect(() => {
        const fetchCountry = async () => {
            if (selectedForm) {
                const lat = mapFormData?.[selectedForm.name]?.["Latitude"];
                const lng = mapFormData?.[selectedForm.name]?.["Longitude"];

                if (lat && lng) {
                    try {
                        const country = await getCountry(+lat, +lng);

                        if (country === 'AU' || country === 'CX' || country === 'CC' || country === 'NF' || country === 'FK') {
                            latField.setValue(lat);
                            lngField.setValue(lng);
                        }
                        else {
                            addAlert({
                                content: <AlertTitle>
                                    Your coordinates are outside Australia, please try again.
                                </AlertTitle>,
                                dismissable: true,
                                severity: 'error',
                                timeout: ERROR_TIMEOUT
                            });
                            latField.setValue(undefined);
                            lngField.setValue(undefined);
                        }
                    } catch (error) {
                        console.error("Error fetching country:", error);
                    }
                }
            }
        };

        fetchCountry();

    }, [mapFormData, selectedForm, latField, lngField]);

    const recordToEditInitRef = useRef<boolean>(false);
    useEffect(() => {
        if (!recordToEdit) {
            recordToEditInitRef.current = false;
        }
    }, [recordToEdit]);

    const prevFocusedRef = useRef<typeof focused>();
    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            const prevFocused = prevFocusedRef.current;

            const latKey = selectedForm?.name + ".Latitude";
            const lngKey = selectedForm?.name + ".Longitude";

            const wasFocused =
                prevFocused &&
                (prevFocused[latKey] !== undefined || prevFocused[lngKey] !== undefined);

            const isNowUnfocused =
                focused[latKey] === undefined &&
                focused[lngKey] === undefined;

            const shouldInitFromRecord =
                recordToEdit && !recordToEditInitRef.current;

            if (selectedForm?.name && (wasFocused || shouldInitFromRecord) && isNowUnfocused) {
                recordToEditInitRef.current = true;

                setMapFormData(prev => {
                    const currentLat = prev[selectedForm!.name]?.Latitude;
                    const currentLng = prev[selectedForm!.name]?.Longitude;

                    const newLat = +latField.getValue();
                    const newLng = +lngField.getValue();

                    if (currentLat === newLat && currentLng === newLng) {
                        return prev;
                    }

                    return {
                        ...prev,
                        [selectedForm!.name]: {
                            ...prev[selectedForm!.name],
                            Latitude: newLat,
                            Longitude: newLng
                        }
                    };
                });
            }

            prevFocusedRef.current = focused;
        });

        return () => cancelAnimationFrame(frame);
    }, [focused]);

    useEffect(() => {
        if (!drawerOpen) {
            form.setValue({}, true);
        }
    }, [drawerOpen]);

    const subSpecies = useMemo(() => {

        switch (species) {
            case Species.NewPests:
                if (selectedForm) {
                    if (value[selectedForm?.name]?.["Species"] == 1) {
                        return "snake";
                    }
                }
                return undefined;
            case Species.OtherPests:
                if (selectedForm) {
                    if (value[selectedForm?.name]?.["SubSpeciesId"] == 1) {
                        return "buffalo";
                    }
                    else if (value[selectedForm?.name]?.["SubSpeciesId"] == 3) {
                        return "donkeys";
                    }
                }
                return undefined;
            case Species.FeralFish:
                if (selectedForm) {
                    if (value[selectedForm?.name]?.["FeralFishSpecies"] == 1) {
                        return "GoldFish";
                    }
                    else if (value[selectedForm?.name]?.["FeralFishSpecies"] == 2) {
                        return "Gambusia";
                    }
                    else if (value[selectedForm?.name]?.["FeralFishSpecies"] == 3) {
                        return "WeatherLoach";
                    }
                    else if (value[selectedForm?.name]?.["FeralFishSpecies"] == 4) {
                        return "Redfin";
                    }
                    else if (value[selectedForm?.name]?.["FeralFishSpecies"] == 5) {
                        return "Tilapia";
                    }
                    else if (value[selectedForm?.name]?.["FeralFishSpecies"] == 6) {
                        return "Tilapia";
                    }
                }
                return undefined;
            default:
                return undefined;
        }

    }, [value, species, selectedForm]);

    useEffect(() => {
        setSubSpecies(subSpecies);
    }, [subSpecies, setSubSpecies]);

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                justifyContent: 'stretch'
            }}
        >
            <LocationAlert />

            {/* -- Header -- */}
            <Box position='relative' width='100%' mt='2px'>
                <Typography
                    textAlign='start'
                    fontSize='1.5rem'
                    fontWeight={600}
                    sx={{
                        color: '#333'
                    }}
                >
                    Record Data
                </Typography>
            </Box>
            <Center sx={{ position: 'absolute', right: '15px', top: '0px' }}>
                <IconButton
                    sx={{
                        heigh: '40px',
                        width: '40px'
                    }}
                    onClick={() => handleClose(species)}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </IconButton>
            </Center>

            {/* -- Form Content -- */}
            {
                speciesFormsLoading ?
                    <Center display='flex' height='100%'>
                        <CircularProgress />
                    </Center> :
                    <Box
                        sx={{
                            marginTop: '15px',
                            marginBottom: '15px'
                        }}
                    >
                        <Typography>What information would you like to add?</Typography>
                        <SiteEventTypeRadioGroup
                            sx={{
                                marginTop: '10px',
                                marginLeft: '30px'
                            }}
                            speciesformdata={allSpeciesFormData}
                            value={siteEventValue ?? null}
                            onChange={handleSiteEventChange}
                        />

                        {/* -- Fields -- */}
                        <List sx={{ pl: '15px', width: '100%', listStyleType: 'disc' }}>
                            {
                                selectedForm?.speciesFormQuestions?.orderBy('order').map(q => (
                                    <SpeciesFormQuestion key={`${q.id}-${q.label}`} speciesFormQuestion={q} />
                                ))
                            }
                        </List>
                        {(siteEventValue === SiteEventType.Sighting.toString() ||
                            siteEventValue === SiteEventType.Damage.toString() ||
                            siteEventValue === SiteEventType.Control.toString() ||
                            siteEventValue === SiteEventType.Disease.toString() ||
                            siteEventValue === SiteEventType.Management.toString()) && (
                                <Box sx={{ display: "flex", justifyContent: "center", width: "auto", paddingTop: "20px" }}>
                                    <FormSubmitButton
                                        sx={{
                                            maxWidth: '100px',
                                            minWidth: '100px',
                                            minHeight: '40px',
                                            maxHeight: '40px',
                                            marginRight: '5px',
                                            backgroundColor: theme.palette.primary.main,
                                            color: grey[100],
                                            marginLeft: '30px',

                                            "&:hover": {
                                                backgroundColor: theme.palette.primary.main,
                                                boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
                                            },

                                            "&:active": {
                                                backgroundColor: theme.palette.secondary.main
                                            }
                                        }}
                                        onClick={async (_, formActions) => {
                                            if (!selectedForm?.name) return;

                                            formActions.setFieldValue(`${selectedForm!.name}.meta.onSubmit`, true);
                                            await formActions.submit();
                                            const errors = formActions.getErrors();

                                            if (!Object.keys(errors).some(path => errors[path]?.length > 0)) {
                                                setSiteEventValue('');
                                            }
                                        }}
                                    >
                                        Submit
                                    </FormSubmitButton>
                                    <FormActionButton
                                        sx={{
                                            maxWidth: '100px',
                                            minWidth: '100px',
                                            minHeight: '40px',
                                            maxHeight: '40px',
                                            backgroundColor: 'transparent',
                                            border: `3px solid ${theme.palette.primary.main}`,
                                            color: theme.palette.primary.main,
                                            marginLeft: '30px',

                                            "&:hover": {
                                                backgroundColor: 'transparent',
                                                boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
                                            },

                                            "&:active": {
                                                backgroundColor: theme.palette.secondary.main,
                                                color: 'white'
                                            }
                                        }}
                                        onClick={(_, actions) => {
                                            actions.reset(true);
                                            handleClose(species);
                                        }}
                                    >
                                        Cancel
                                    </FormActionButton>
                                </Box>
                            )}
                    </Box>
            }
        </Box>
    );
}

/*
 * --- Form initial values ---
 * 
 * Keep this outside of the function component to prevent the state from
 * clearing when the form fields change. 
 */
const initial = {};

/**
 * --- Species Form Base ---
 * 
 * Wraps the main species forms radio options and the species form fields. Debug mode provides
 * us views of the current form state and error states.
 */
const SpeciesFormBase: FC = () => {
    const validationDebounce = 500;

    //
    // -- Hooks --
    const { validateSpeciesForm } = useValidateSpeciesForm();
    const { getFormFieldDisplayConditionsValue } = useFormFieldCondition();

    const conversionInit = useRef<boolean>(false);

    //
    // -- Context --
    const { selectedForm, selectedFormFields } = useContext(SpeciesFormsContext);
    const { events, filter, setSubmitterFilter } = useContext(MapContext);
    const { drawerOpen, closeDrawer, recordToEdit } = useContext(MapFormContext);
    const client = useContext(ServiceStackClientContext);
    const { species: { species } } = useContext(SpeciesContext);
    const { addAlert } = useContext(AlertContext);
    const { data: appSettings } = useContext(AppSettingsContext);
    const { user } = useContext(AuthContext);
    const { imageGuid, setAccuracyWarning } = useContext(MapFormContext);

    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]);

    // [IACWEB-1447] - New Pixel for DeerScan
    useEffect(() => {
        if (species === Species.Deer && !conversionInit.current) {
            sendConversion("page_view");
            conversionInit.current = true;
        }
    }, [species]);

    // 
    // -- State --
    const [initialState, setInitialState] = useState<Record<string, any>>({});
    const [currentPos, setCurrentPos] = useState<{ lat: number, lng: number } | undefined>(undefined);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

    // 
    // -- Form validation
    //
    // Triggers for all fields in the form. 
    const validate: IFormValidate<typeof initial, string> = useCallback(async (formState: IFormState<any, string>, _) => {

        const state = validateSpeciesForm(formState, { softValidate: false });

        return state ?? {};
    }, [selectedForm, validateSpeciesForm]);

    // Generate record to edit query
    const recordToEditRequest = useMemo(() => {
        if (!recordToEdit || !recordToEdit?.recordId || !recordToEdit?.siteEventType || !species || species == Species.None) return undefined;

        return {
            species: species,
            siteEventType: recordToEdit.siteEventType,
            id: recordToEdit.recordId
        }
    }, [recordToEdit, species]);

    // Record to edit hook
    const { data: recordToEditData, loading: loadingRecordToEditData } = useGetSiteEventById(
        recordToEditRequest,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const deletableRecord = useMemo(() => {
        if (recordToEditData && (recordToEditData?.userId == user?.id || isAdminOrSuperAdmin)) {
            return true;
        }
        return false;
    }, [recordToEditData, user]);

    // Generate the state request
    const stateRequest = useMemo(() => {
        if (!currentPos) {
            return {};
        }

        var lat = currentPos?.lat;
        if (isNaN(lat) || lat < -90 || lat > 90) {
            return undefined;
        }

        var lng = currentPos?.lng;
        if (isNaN(lng) || lng < -180 || lng > 180) {
            return undefined;
        }

        return {
            latitude: currentPos?.lat.toString(),
            longitude: currentPos?.lng.toString()
        }
    }, [currentPos]);

    // get state hook
    const { data: state } = useGetCoordinatesState(
        stateRequest,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    //
    // -- Validation Debouncer
    //
    // Slows down the trigger rate of validation. Useful for asynchronous
    // validation (e.g., validating fields with backend data)
    const debouncedValidate = useMemo(() => {
        if (!validationDebounce || validationDebounce <= 0) {
            return validate;
        }

        return asyncDebounce(validate, validationDebounce);
    }, [validate]);

    //
    // -- useEffects --
    useEffect(() => {
        if (!currentPos) {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position: GeolocationPosition) => {
                        if (position.coords.accuracy <= 100) {
                            setCurrentPos({
                                lat: JSON.parse(JSON.stringify(position.coords.latitude)),
                                lng: JSON.parse(JSON.stringify(position.coords.longitude)),
                            });
                            setAccuracyWarning(false);
                        }
                        else {
                            setCurrentPos(undefined);
                            setAccuracyWarning(true);
                        }
                    },
                    () => {},
                    {
                        enableHighAccuracy: true
                    }
                );

                // Else, notify user that this is not working
            } else {
                // TODO: Handle navigator error
            }
        }
    }, []);
    
    useEffect(() => {
        setInitialState(c => {
            if (!selectedForm) return c;

            const existingFormState = c[selectedForm.name];
            const hasInitialised = existingFormState?.meta?.hasInitialised;

            // Edit mode
            if (recordToEditData && !hasInitialised) {
                const additionalFields: Record<string, any> = {};

                if (imageGuid) {
                    additionalFields.guid = imageGuid;
                }

                if (
                    species === Species.Rabbit &&
                    'rabbitSpotlightAbundanceScore' in recordToEditData &&
                    recordToEditData.rabbitSpotlightAbundanceScore
                ) {
                    additionalFields.SpotlightCheckbox = true;
                }
                else if (
                    species === Species.FeralCat && (
                        ('feralCatHabitatRoadside' in recordToEditData && recordToEditData.feralCatHabitatRoadside) ||
                        ('feralCatHabitatVegetation' in recordToEditData && recordToEditData.feralCatHabitatVegetation) ||
                        ('feralCatHabitatFarmland' in recordToEditData && recordToEditData.feralCatHabitatFarmland) ||
                        ('feralCatHabitatOther' in recordToEditData && recordToEditData.feralCatHabitatOther)
                    )
                ) {
                    additionalFields.HabitatCheckbox = true;
                }
                else if (species === Species.FeralFish) {

                    if ('subSpeciesId' in recordToEditData && recordToEditData.subSpeciesId) {
                        additionalFields.FeralFishSpecies = recordToEditData.subSpeciesId;
                    }

                    if (
                        ('feralFishHabitat' in recordToEditData && recordToEditData.feralFishHabitat) ||
                        ('microHabitatLogsSnags' in recordToEditData && recordToEditData.microHabitatLogsSnags) ||
                        ('microHabitatAmongRocks' in recordToEditData && recordToEditData.microHabitatAmongRocks) ||
                        ('microHabitatOpenWaterAtSurface' in recordToEditData && recordToEditData.microHabitatOpenWaterAtSurface) ||
                        ('microHabitatAmongWaterPlants' in recordToEditData && recordToEditData.microHabitatAmongWaterPlants) ||
                        ('microHabitatAtWatersEdge' in recordToEditData && recordToEditData.microHabitatAtWatersEdge) ||
                        ('microHabitatOther' in recordToEditData && recordToEditData.microHabitatOther) ||
                        ('habitatDegradationTurbidMuddyWater' in recordToEditData && recordToEditData.habitatDegradationTurbidMuddyWater) ||
                        ('habitatDegradationInstreamBankErosion' in recordToEditData && recordToEditData.habitatDegradationInstreamBankErosion) ||
                        ('habitatDegradationErosionInRiparianZone' in recordToEditData && recordToEditData.habitatDegradationErosionInRiparianZone) ||
                        ('habitatDegradationWillowsPresent' in recordToEditData && recordToEditData.habitatDegradationWillowsPresent) ||
                        ('habitatDegradationLivestockPresent' in recordToEditData && recordToEditData.habitatDegradationLivestockPresent) ||
                        ('habitatDegradationSiltSandBuildUp' in recordToEditData && recordToEditData.habitatDegradationSiltSandBuildUp) ||
                        ('habitatDegradationNoDegradation' in recordToEditData && recordToEditData.habitatDegradationNoDegradation) ||
                        ('habitatDegradationOther' in recordToEditData && recordToEditData.habitatDegradationOther)
                    ) {
                        additionalFields.HabitatCheckbox = true;
                    }
                }
                
                return {
                    ...c,
                    [selectedForm.name]: {
                        meta: {
                            onSubmit: false,
                            hasInitialised: true,
                            state: state,
                            hasImage: recordToEdit?.hasImage,
                            ...existingFormState?.meta
                        },
                        ...capitaliseKeys(
                            Object.fromEntries(
                                Object.entries(recordToEditData)
                                    .filter(([key]) => {
                                        return Object.keys(selectedFormFields!)
                                            .map(k => k.toLowerCase())
                                            .includes(key.toLowerCase()) ||
                                            Object.values(selectedFormFields!)
                                                .some((field: any) =>
                                                    field?.meta?.targetName?.toLowerCase() === key.toLowerCase()
                                                );
                                    })
                                    .map(([key, value]) => {
                                        const capitalizedKey = key.capitaliseFirstLetter();
                                        const fieldType = (selectedFormFields![capitalizedKey] as any)?.__type;

                                        if (fieldType?.includes('RadioGroup')) {
                                            return [key, String(value)];
                                        }

                                        return [key, value];
                                    })
                            )
                        ),
                        ...additionalFields,
                        ParentId: recordToEditData.id,
                        PlatformType: '0',
                        PrivateData: recordToEditData?.privateData ? 1 : 0,
                        UserId: recordToEditData?.userId,
                        Email: recordToEditData?.email
                    }
                };
            }

            // New form initalisation
            if (!hasInitialised && !loadingRecordToEditData) {
                return {
                    ...c,
                    [selectedForm.name]: {
                        ...existingFormState,
                        meta: {
                            onSubmit: false,
                            hasInitialised: true,
                            state: state,
                            ...existingFormState?.meta
                        },
                        Latitude: currentPos?.lat?.toString(),
                        Longitude: currentPos?.lng?.toString(),
                        PrivateData: "0",
                        StartDateTime: dayjs(),
                        PlatformType: '0',
                        ...(selectedFormFields
                            ? Object.keys(selectedFormFields).reduce<Record<string, string>>((a, b) => {
                                if (a[b]) return a;

                                const field: any = selectedFormFields[b];

                                if (field?.default !== undefined) {
                                    a[b] = field.default;
                                }
                                else if (field?.defaultValue !== undefined) {
                                    a[b] = field.defaultValue;
                                }
                                else if (field?.checked !== undefined) {
                                    a[b] = field.checked;
                                }

                                return a;
                            }, {})
                            : {})
                    }
                };
            }

            return c;
        });
    }, [
        currentPos,
        selectedForm,
        recordToEditData,
        selectedFormFields,
        species,
        state,
        loadingRecordToEditData
    ]);

    useEffect(() => {
        // Clear form values on drawer close
        if (!drawerOpen) {
            setInitialState(({}));
        }
    }, [drawerOpen]);

    const handleCloseDrawer = () => {
        if (filter.bySubmitter.prevState) {
            setSubmitterFilter(c => ({
                ...c,
                ...filter.bySubmitter.prevState
            }));
        }

        closeDrawer();
    }

    const onSubmit: IFormSubmit<Record<string, any>, string> = useCallback(async (formState, formActions) => {
        let failed = false;

        const isDisplayed = getFormFieldDisplayConditionsValue(formState);

        if (!formState.value || !selectedForm?.name) {
            addAlert({
                content: <AlertTitle>
                    An error occurred while submitting your form.
                </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
            return;
        }

        let mutatedFormState = {
            ...formState,
            value: {
                ...formState.value,
                [selectedForm.name]: {
                    ...formState.value?.[selectedForm.name]
                }
            }
        };

        if (!recordToEdit && isDisplayed) {
            mutatedFormState.value[selectedForm.name] = {
                ...mutatedFormState.value[selectedForm.name],
                ...(Object.keys(isDisplayed)
                    .filter(x => !isDisplayed[x])
                    .reduce<Record<string, any>>((a, b) => {
                        a[b] = undefined;
                        return a;
                    }, {})),
                meta: formState.value[selectedForm.name]?.meta,
                Email: formState.value[selectedForm.name]?.Email
            };
        }

        if (species === Species.FeralFish && mutatedFormState.value) {
            mutatedFormState.value[selectedForm.name].SubSpeciesId =
                mutatedFormState.value[selectedForm.name].FeralFishSpecies;
        }
        else if (
            species === Species.Toad &&
            mutatedFormState.value &&
            mutatedFormState.value[selectedForm.name]?.meta?.state === 'NSW'
        ) {
            mutatedFormState.value[selectedForm.name].Email =
                mutatedFormState.value[selectedForm.name].EmailNsw;
        }

        const validation = validateSpeciesForm(mutatedFormState, { softValidate: false });

        if (validation && Object.keys(validation).length > 0) {
            formActions.setErrors(validation, true);
            return;
        }

        if (mutatedFormState.value && selectedForm?.name) {
            try {
                await client.post(new NewPostInsertSiteEvent({
                    species: species,
                    siteEventType: selectedForm.siteEventType,
                    formData: mutatedFormState.value[selectedForm.name]
                }));
            }
            catch {
                failed = true;

                addAlert({
                    content: <AlertTitle>
                        An error occurred while submitting your form.
                    </AlertTitle>,
                    dismissable: true,
                    severity: 'error',
                    timeout: ERROR_TIMEOUT
                });
            }
            finally {
                // refresh map markers
                await events.trigger(MapEvent.Refresh)
                handleCloseDrawer();
                if (!failed) {
                    addAlert({
                        content: <AlertTitle>
                            Thank you, your form has been successfully submitted.
                        </AlertTitle>,
                        dismissable: true,
                        severity: 'success',
                        timeout: SUCCESS_TIMEOUT
                    });

                    // [IACWEB-1447] - New Pixel for DeerScan
                    if (species === Species.Deer) {
                        sendConversion("custom_2");
                    }
                }
            }
        }
        return {};
    }, [client, selectedForm, validateSpeciesForm, addAlert, handleCloseDrawer, events, recordToEdit]);

    const onSubmitFailure = useCallback(async (formState: IFormState<any, string>, formActions: IFormActions<{}, string>, _: any) => {

        if (!selectedForm?.name) return;

        const onSubmit = (formState.value?.[selectedForm!.name]?.['meta']?.['onSubmit'] ?? false) as boolean

        if (onSubmit) {
            formActions.setFieldValue(`${selectedForm!.name}.meta.onSubmit`, false);
        }
    }, [selectedForm]);

    const onSubmitValidationFailure: IFormSubmitValidationFailed<any, string> = useCallback(async (formState, formActions) => {
        addAlert({
            content: <AlertTitle>
                Please fix the errors on the form before submitting.
            </AlertTitle>,
            dismissable: true,
            severity: 'error',
            timeout: ERROR_TIMEOUT
        });

        if (!selectedForm?.name) return;

        const onSubmit = (formState.value?.[selectedForm!.name]?.['meta']?.['onSubmit'] ?? false) as boolean

        if (onSubmit) {
            formActions.setFieldValue(`${selectedForm!.name}.meta.onSubmit`, false);
        }

    }, [selectedForm, addAlert]);

    const handleDelete = async () => {
        try {
            await client.post(new DeleteSiteEvent({
                species: species,
                siteEventType: recordToEditData?.sightingTypeId,
                siteEventId: recordToEditData?.id
            }));
        }
        catch (e) {          
            addAlert({
                content: <AlertTitle>
                    An error occurred while deleting your record.
                </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
        finally {
            await events.trigger(MapEvent.Refresh);
            
            closeDrawer();
            setOpenDeleteModal(false);

            addAlert({
                content: <AlertTitle>
                    Your record has been successfully deleted.
                </AlertTitle>,
                dismissable: true,
                severity: 'success',
                timeout: SUCCESS_TIMEOUT
            });
        }
    };

    return (
        <>
            <FieldSpacer />
            <Box
                sx={{
                    height: '100%',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    padding: '0px 15px',
                    overflowY: 'scroll'
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FormErrorBoundary mode={FormErrorBoundaryMode.Contain}>
                        <Form
                            initialValue={initialState}
                            onValidate={debouncedValidate}
                            validateOn={ValidateOn.onChange}
                            ConditionConfiguration={conditionConfiguration}
                            onSubmit={onSubmit}
                            onSubmitFailed={onSubmitFailure}
                            onSubmitValidationFailed={onSubmitValidationFailure}
                        >
                            <SpeciesFormBaseContent />
                            {deletableRecord && (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            paddingBottom: '20px',
                                            marginLeft: '30px'
                                        }}
                                    >
                                        <Button 
                                            variant='outlined'
                                            onClick={() => setOpenDeleteModal(true)}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                    <Modal
                                        open={openDeleteModal}
                                        onClose={() => setOpenDeleteModal(false)}
                                        aria-labelledby="delete-modal-title"
                                        aria-describedby="delete-modal-description"
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                bgcolor: 'background.paper',
                                                boxShadow: 24,
                                                borderRadius: 2,
                                                p: 4,
                                                minWidth: 320,
                                                textAlign: 'center'
                                            }}
                                        >
                                            <h2 id="delete-modal-title">CONFIRM DELETE</h2>

                                            <p id="delete-modal-description">
                                                Are you sure you wish to remove this record?
                                            </p>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    gap: 2,
                                                    mt: 3
                                                }}
                                            >
                                                <Button
                                                    sx={{
                                                        color: 'white'
                                                    }}
                                                    variant="contained"
                                                    onClick={() => handleDelete()}
                                                >
                                                    Delete
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setOpenDeleteModal(false)}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Modal>

                                </>
                            )}
                            {
                                appSettings?.speciesForms?.debugMode && (
                                    <>
                                        <CustomFormValues />
                                        <FieldSpacer />
                                        <CustomFormErrors />
                                        <FieldSpacer />
                                    </>
                                )}
                        </Form>
                    </FormErrorBoundary>
                </LocalizationProvider>
            </Box>
        </>
    );
}

export default SpeciesFormBase;